import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { faceCounts, hasOfAKind, hasStraight, reroll, rollDice, sumDice, validHold } from '@campfire/dice';

export const UPPER = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'] as const;
export const LOWER = ['threeKind', 'fourKind', 'fullHouse', 'smallStraight', 'largeStraight', 'yahtzee', 'chance'] as const;
export const CATEGORIES = [...UPPER, ...LOWER] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Readonly<Record<Category, string>> = {
  ones: 'Ones',
  twos: 'Twos',
  threes: 'Threes',
  fours: 'Fours',
  fives: 'Fives',
  sixes: 'Sixes',
  threeKind: '3 of a kind',
  fourKind: '4 of a kind',
  fullHouse: 'Full house',
  smallStraight: 'Small straight',
  largeStraight: 'Large straight',
  yahtzee: 'Yahtzee',
  chance: 'Chance',
};

export const UPPER_BONUS_AT = 63;
export const UPPER_BONUS = 35;
export const YAHTZEE_BONUS = 100;
export const ROLLS = 3;

export interface YahtzeeOptions {
  /** forced: a repeat Yahtzee must go in its upper box, else a lower box, else a zero (standard). free: any open box. */
  readonly jokers: 'forced' | 'free';
}

export type Sheet = Readonly<Record<Category, number | null>>;

export type YahtzeeEvent =
  | { readonly kind: 'roll'; readonly seat: Seat; readonly rollsLeft: number }
  | { readonly kind: 'score'; readonly seat: Seat; readonly category: Category; readonly points: number; readonly bonus: boolean; readonly joker: boolean };

export interface YahtzeeState {
  readonly options: YahtzeeOptions;
  readonly sheets: readonly Sheet[];
  /** Extra-Yahtzee bonuses earned (each worth 100). */
  readonly bonuses: readonly number[];
  /** Empty before the turn's first roll. */
  readonly dice: readonly number[];
  /** Dice kept for the next roll, as indexes into `dice`; everyone sees them (FR-YAHTZEE-12). */
  readonly held: readonly number[];
  readonly rollsLeft: number;
  readonly turn: Seat;
  readonly round: number;
  readonly last: YahtzeeEvent | null;
  readonly result: Outcome | null;
}

export type YahtzeeAction =
  /** Roll the dice not held. `hold` overrides the table's held dice (older clients, bots). */
  | { readonly type: 'roll'; readonly hold?: readonly number[] }
  /** Tap a die to keep it, or tap again to roll it after all. */
  | { readonly type: 'hold'; readonly index: number }
  | { readonly type: 'score'; readonly category: Category };

export interface YahtzeeView {
  readonly you: Seat | null;
  readonly dice: readonly number[];
  readonly held: readonly number[];
  readonly rollsLeft: number;
  readonly turn: Seat;
  readonly round: number;
  readonly sheets: readonly Sheet[];
  readonly bonuses: readonly number[];
  readonly totals: readonly number[];
  readonly upper: readonly number[];
  /** What the current dice would score in each open box for the seat on turn (null before the first roll). */
  readonly potentials: Readonly<Partial<Record<Category, number>>> | null;
  /** Boxes the seat on turn may score in right now. */
  readonly legal: readonly Category[];
  readonly jokers: 'forced' | 'free';
  readonly last: YahtzeeEvent | null;
  readonly result: Outcome | null;
}

const emptySheet = (): Sheet => Object.fromEntries(CATEGORIES.map((c) => [c, null])) as Sheet;
const isYahtzee = (dice: readonly number[]): boolean => dice.length === 5 && hasOfAKind(dice, 5);

/** Points for `dice` in `cat`. With `joker`, fixed-value boxes pay in full. */
export function scoreFor(cat: Category, dice: readonly number[], joker = false): number {
  const counts = faceCounts(dice);
  const upper = (UPPER as readonly string[]).indexOf(cat);
  if (upper >= 0) return (counts[upper + 1] ?? 0) * (upper + 1);
  switch (cat) {
    case 'threeKind':
      return hasOfAKind(dice, 3) ? sumDice(dice) : 0;
    case 'fourKind':
      return hasOfAKind(dice, 4) ? sumDice(dice) : 0;
    case 'fullHouse':
      return joker || (counts.includes(3) && counts.includes(2)) ? 25 : 0;
    case 'smallStraight':
      return joker || hasStraight(dice, 4) ? 30 : 0;
    case 'largeStraight':
      return joker || hasStraight(dice, 5) ? 40 : 0;
    case 'yahtzee':
      return isYahtzee(dice) ? 50 : 0;
    default:
      return sumDice(dice);
  }
}

/** Is this roll a joker: a Yahtzee while the Yahtzee box is already filled? */
const isJoker = (sheet: Sheet, dice: readonly number[]): boolean => sheet.yahtzee !== null && isYahtzee(dice);

/** Boxes legal for these dice, applying the joker rule. */
export function legalCategories(sheet: Sheet, dice: readonly number[], jokers: 'forced' | 'free'): Category[] {
  const open = CATEGORIES.filter((c) => sheet[c] === null);
  if (dice.length === 0) return [];
  if (!isJoker(sheet, dice) || jokers === 'free') return open;
  const matching = UPPER[(dice[0] as number) - 1] as Category;
  if (open.includes(matching)) return [matching];
  const lower = open.filter((c) => (LOWER as readonly string[]).includes(c));
  return lower.length > 0 ? lower : open;
}

export const upperSum = (sheet: Sheet): number => UPPER.reduce((a, c) => a + (sheet[c] ?? 0), 0);

export function totalOf(sheet: Sheet, bonuses: number): number {
  const all = CATEGORIES.reduce((a, c) => a + (sheet[c] ?? 0), 0);
  return all + (upperSum(sheet) >= UPPER_BONUS_AT ? UPPER_BONUS : 0) + bonuses * YAHTZEE_BONUS;
}

const full = (sheet: Sheet) => CATEGORIES.every((c) => sheet[c] !== null);

export const yahtzee: GameDefinition<YahtzeeState, YahtzeeAction, YahtzeeView, YahtzeeOptions> = {
  id: 'yahtzee',
  name: 'Yahtzee',
  rules: [
    'Roll five dice to fill a scorecard of 13 boxes. The highest total after everyone has filled their card wins. One player may play alone for a personal best.',
    'On your turn roll up to three times. After the first and second roll choose which dice to hold and roll the rest. Then you must score the dice in one empty box, even if it scores nothing.',
    'Upper boxes (Ones to Sixes) score the total of the dice with that number. Score 63 or more in the upper section for a 35 bonus.',
    '3 of a kind and 4 of a kind score the total of all five dice. Full house is 25, small straight (four in a row) 30, large straight (five in a row) 40, Yahtzee (five of a kind) 50. Chance is the sum of all dice.',
    'The app shows what the current dice would score in each open box.',
    'Extra Yahtzees: roll another Yahtzee after scoring 50 in the Yahtzee box and you get a 100 bonus. The dice then act as a joker and score in full in a full house or straight box.',
    'Setting: Extra Yahtzees. Forced joker (standard) means you must use the matching upper box if open, then any open lower box, then any open upper box (scoring zero). Free joker lets you score in any open box.',
    'A second Yahtzee scored after a zero in the Yahtzee box earns no bonus but is still a joker.',
    'The app enforces all of this. There is nothing for the table to agree on.',
  ],
  minSeats: 1,
  maxSeats: 6,
  defaultOptions: { jokers: 'forced' },
  optionSpecs: [
    {
      key: 'jokers',
      label: 'Extra Yahtzees',
      help: 'A second Yahtzee scores a 100 bonus (if you scored 50 in Yahtzee) and acts as a joker.',
      choices: [
        { value: 'forced', label: 'Forced joker (standard)' },
        { value: 'free', label: 'Free joker: score in any open box' },
      ],
    },
  ],

  setup: ({ seats, options }) => ({
    options,
    sheets: Array.from({ length: seats }, emptySheet),
    bonuses: Array.from({ length: seats }, () => 0),
    dice: [],
    held: [],
    rollsLeft: ROLLS,
    turn: 0,
    round: 1,
    last: null,
    result: null,
  }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    if (a.type === 'hold') {
      if (s.dice.length === 0) return { ok: false, reason: 'roll first' };
      if (s.rollsLeft === 0) return { ok: false, reason: 'no rolls left: score a box' };
      return Number.isInteger(a.index) && a.index >= 0 && a.index < s.dice.length ? { ok: true } : { ok: false, reason: 'no such die' };
    }
    if (a.type === 'roll') {
      if (s.rollsLeft === 0) return { ok: false, reason: 'no rolls left: score a box' };
      const hold = a.hold ?? (s.held ?? []);
      if (s.dice.length === 0) return hold.length === 0 ? { ok: true } : { ok: false, reason: 'nothing to hold yet' };
      if (!validHold(s.dice, hold)) return { ok: false, reason: 'bad hold' };
      if (hold.length >= s.dice.length) return { ok: false, reason: 'keep fewer than five dice to roll' };
      return { ok: true };
    }
    if (a.type !== 'score') return { ok: false, reason: 'unknown action' };
    if (s.dice.length === 0) return { ok: false, reason: 'roll first' };
    const sheet = s.sheets[seat] as Sheet;
    if (!(CATEGORIES as readonly string[]).includes(a.category)) return { ok: false, reason: 'unknown box' };
    if (sheet[a.category] !== null) return { ok: false, reason: 'that box is used' };
    if (!legalCategories(sheet, s.dice, s.options.jokers).includes(a.category)) return { ok: false, reason: 'the joker rule requires another box' };
    return { ok: true };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'hold') {
      const held = s.held ?? [];
      return { ...s, held: held.includes(a.index) ? held.filter((i) => i !== a.index) : [...held, a.index].sort((x, y) => x - y) };
    }
    if (a.type === 'roll') {
      const hold = a.hold ?? (s.held ?? []);
      const dice = s.dice.length === 0 ? rollDice(5, rng) : reroll(s.dice, hold, rng);
      const rollsLeft = s.rollsLeft - 1;
      // Held dice stay held for the next roll, as at a real table.
      return { ...s, dice, held: s.dice.length === 0 ? [] : [...hold].sort((x, y) => x - y), rollsLeft, last: { kind: 'roll', seat, rollsLeft } };
    }
    const sheet = s.sheets[seat] as Sheet;
    const joker = isJoker(sheet, s.dice);
    const bonus = joker && sheet.yahtzee === 50;
    const points = scoreFor(a.category, s.dice, joker);
    const sheets = s.sheets.map((sh, i) => (i === seat ? { ...sh, [a.category]: points } : sh));
    const bonuses = s.bonuses.map((b, i) => (i === seat && bonus ? b + 1 : b));
    const next = (seat + 1) % sheets.length;
    const round = next === 0 ? s.round + 1 : s.round;
    const base: YahtzeeState = {
      ...s,
      sheets,
      bonuses,
      dice: [],
      held: [],
      rollsLeft: ROLLS,
      turn: next,
      round,
      last: { kind: 'score', seat, category: a.category, points, bonus, joker },
    };
    if (!sheets.every(full)) return base;
    const totals = sheets.map((sh, i) => totalOf(sh, bonuses[i] as number));
    const best = Math.max(...totals);
    const winners = totals.flatMap((t, i) => (t === best ? [i] : []));
    return { ...base, round: 13, result: { winners, draw: false, reason: 'card full', scores: { ...totals } } };
  },

  view(s, viewer: Viewer): YahtzeeView {
    const sheet = s.sheets[s.turn] as Sheet;
    const legal = s.result ? [] : legalCategories(sheet, s.dice, s.options.jokers);
    const joker = isJoker(sheet, s.dice);
    const potentials =
      s.dice.length === 0 || s.result
        ? null
        : Object.fromEntries(CATEGORIES.filter((c) => sheet[c] === null).map((c) => [c, scoreFor(c, s.dice, joker)]));
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      dice: s.dice,
      held: s.result ? [] : (s.held ?? []),
      rollsLeft: s.rollsLeft,
      turn: s.turn,
      round: s.round,
      sheets: s.sheets,
      bonuses: s.bonuses,
      totals: s.sheets.map((sh, i) => totalOf(sh, s.bonuses[i] as number)),
      upper: s.sheets.map(upperSum),
      potentials,
      legal,
      jokers: s.options.jokers,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    if (s.dice.length === 0) return [{ type: 'roll' }];
    const out: YahtzeeAction[] = legalCategories(s.sheets[seat] as Sheet, s.dice, s.options.jokers).map((category) => ({ type: 'score', category }));
    if (s.rollsLeft > 0) {
      for (let mask = 0; mask < 31; mask++) {
        out.push({ type: 'roll', hold: [0, 1, 2, 3, 4].filter((i) => mask & (1 << i)) });
      }
    }
    return out;
  },
};
